"use client";

import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  CardMedia,
  CircularProgress,
  Link,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Edit,
  ContentCopy as ContentCopyIcon, // Import for copy icon
  Facebook as FacebookIcon, // Import for Facebook
  Twitter as TwitterIcon, // Import for Twitter
  LinkedIn as LinkedInIcon, // Import for LinkedIn
  WhatsApp as WhatsAppIcon,
  Download,
  PictureAsPdf, Link as LinkIcon, Archive, PlayArrow,
  Image
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router";
import bg1 from "../../assets/bg1.jpg";
import { useUser } from "@/contexts/UserContext";
import { apiRemote, IHost } from "@/api";
import { useAlert } from "@/contexts/AlertContext";

const STATUS_DRAFT = "draft";
const STATUS_PUBLISHED = "published";
const STATUS_FULL = "full";
const STATUS_COMPLETED = "completed";
const STATUS_IN_PROGRESS = "in_progress";
const STATUS_CANCELLED = "cancelled";

export default function WorkshopDetail() {
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const alert = useAlert();
  const [loading, setLoading] = useState<boolean>(true);
  const [workshop, setWorkshop] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleRegistration = () => {
    if (user?.ID) {
      setIsSubmitting(true);
      apiRemote
        .registerWorkshop({
          username: user.user_login,
          workshop_id: id,
          user_email: user.user_email,
          userTimeZone
        })
        .then((data) => {
          if (data.code == 201) {
            setSuccessOpen(true);
            setRegistered(true);
          } else {
            alert.error(data.message);
          }
        })
        .catch((err) => {
          alert.error(err.message);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      // Nếu user chưa login, hiển thị dialog đăng ký/đăng nhập
      setRegistrationOpen(true);
    }
  };

  const handleShare = () => {
    setShareOpen(true);
  };

  const currentWorkshopUrl = typeof window !== 'undefined' ? window.location.href : '';
  const workshopTitle = workshop?.title || "Workshop";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentWorkshopUrl);
      alert.success("Link copied to clipboard! 📋");
      setShareOpen(false); // Close the dialog after copying
    } catch (err) {
      alert.error("Failed to copy link. Please try again.");
      console.error("Failed to copy: ", err);
    }
  };

  const handleShareToFacebook = () => {
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentWorkshopUrl)}`;
    window.open(facebookShareUrl, '_blank');
  };

  const handleShareToTwitter = () => {
    const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this workshop: ${workshopTitle}`)}&url=${encodeURIComponent(currentWorkshopUrl)}`;
    window.open(twitterShareUrl, '_blank');
  };

  const handleShareToLinkedIn = () => {
    const linkedInShareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(currentWorkshopUrl)}&title=${encodeURIComponent(workshopTitle)}&summary=${encodeURIComponent(workshop.description || '')}&source=${encodeURIComponent(window.location.hostname)}`;
    window.open(linkedInShareUrl, '_blank');
  };

  // const handleShareViaEmail = () => {
  //   const subject = encodeURIComponent(`Check out this workshop: ${workshopTitle}`);
  //   const body = encodeURIComponent(`I thought you might be interested in this workshop:\n\n${workshopTitle}\n${currentWorkshopUrl}\n\n${workshop.description || ''}`);
  //   window.location.href = `mailto:?subject=${subject}&body=${body}`;
  // };

  const handleShareToWhatsApp = () => {
    const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(`Check out this workshop: ${workshopTitle} - ${currentWorkshopUrl}`)}`;
    window.open(whatsappShareUrl, '_blank');
  };

  const handleEdit = () => {
    // Điều hướng đến trang chỉnh sửa workshop
    // Bạn cần thay đổi '/edit-workshop/${id}' thành đường dẫn thực tế của trang chỉnh sửa của bạn
    navigate(`../edit/${id}`);
  };

  useEffect(() => {
    if (!id) {
      setLoading(false); // No ID, so stop loading
      return;
    }
    // setLoading(true);
    apiRemote
      .getWorkshopById(id, user?.user_login, userTimeZone)
      .then((data) => {
        if (data && data.data) {
          const processedDescription = data.data.description.replace(
            /\\'/g,
            "'"
          );

          let parsedHosts: IHost[] = [];
          if (data.data.hosts && data.data.hosts !== "[]") {
              try {
                  parsedHosts = JSON.parse(data.data.hosts);
              } catch (e) {
                  console.error("Error parsing hosts string:", e);
              }
          } else if (data.data.host_name) {
              // Backward compatibility for old data
              parsedHosts = [{
                  host_name: data.data.host_name,
                  host_email: data.data.host_email,
                  host_organization: data.data.host_organization,
                  host_bio: data.data.host_bio_snippet,
              }];
          }
          setWorkshop({ ...data.data, description: processedDescription, hosts: parsedHosts });
          setRegistered(data.data.is_registered || false);
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to add new workshop.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!workshop && !loading) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="h5" color="error">
          Workshop not found.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("..")}
          sx={{ mt: 3 }}
        >
          Back to All Workshops
        </Button>
      </Container>
    );
  }

  // Function to parse learning objectives
  const parseLearningObjectives = (objectivesString: string) => {
    return objectivesString
      .split("\r\n")
      .filter(Boolean)
      .map((obj) => obj.replace(/^- /, ""));
  };

  const learningObjectives = workshop.learning_objectives
    ? parseLearningObjectives(workshop.learning_objectives)
    : [];

  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const workshopDateTime = new Date(workshop.date);
  const currentDateTime = new Date();

  const isPastWorkshop = workshopDateTime < currentDateTime; // true nếu thời gian workshop đã trôi qua

  const isCurrentUserTheCreator = user?.user_login == workshop.created_by;

  let buttonText = "";
  let isButtonDisabled = false;
  let buttonVariant: "text" | "outlined" | "contained" = "contained";
  let buttonIcon = null;

  if (registered) {
    if (workshop.calculated_status === STATUS_CANCELLED || workshop.status === STATUS_CANCELLED) {
      buttonText = "Cancelled";
      isButtonDisabled = true;
      // buttonVariant và buttonIcon có thể giữ nguyên hoặc thay đổi tùy vào thiết kế
      buttonVariant = "outlined"; // Hoặc một variant khác cho trạng thái hủy
      buttonIcon = null; // Hoặc một icon phù hợp cho trạng thái hủy
    } else if (workshop.calculated_status === STATUS_COMPLETED) {
      buttonText = "Completed";
      isButtonDisabled = true;
      buttonVariant = "outlined";
      buttonIcon = null;
    } else if (workshop.calculated_status === STATUS_IN_PROGRESS) {
      buttonText = "In Progress";
      isButtonDisabled = true;
      buttonVariant = "outlined";
      buttonIcon = null;
    } else if (workshop.calculated_status === STATUS_DRAFT || workshop.status === STATUS_DRAFT) {
      buttonText = "Draft";
      isButtonDisabled = true;
      buttonVariant = "outlined";
      buttonIcon = null;
    } else {
      buttonText = "Registered";
      isButtonDisabled = true;
      buttonVariant = "outlined";
      buttonIcon = <CheckCircleIcon />;
    }
  } 
  // else if (isPastWorkshop) {
  //   buttonText = "Event Ended";
  //   isButtonDisabled = true;
  // } 
  else {
    if(workshop.status == STATUS_DRAFT){
      buttonText = "Draft";
      isButtonDisabled = true;
    }else if(workshop.status == STATUS_CANCELLED){
      buttonText = "Cancelled";
      isButtonDisabled = true;
    }else if(workshop.status == STATUS_FULL){
      buttonText = "Full";
      isButtonDisabled = true;
      buttonVariant = "outlined";
    }else if(workshop.calculated_status == STATUS_PUBLISHED){
      buttonText = "Register Now";
      isButtonDisabled = isSubmitting;
    }else if(workshop.calculated_status == STATUS_COMPLETED){
      buttonText = "Completed";
      isButtonDisabled = true;
    }else if(workshop.calculated_status == STATUS_IN_PROGRESS){
      buttonText = "In Progress";
      isButtonDisabled = true;
    }else{
      buttonText = "Register Now";
      isButtonDisabled = isSubmitting;
    }
  }

  // Hàm để trả về icon dựa trên loại file
  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf")) {
      return <PictureAsPdf color="primary" />;
    }
    if (fileType.includes("image")) {
      return <Image color="primary" />;
    }
    if (fileType.includes("video")) {
      return <PlayArrow color="primary" />;
    }
    return <Archive color="primary" />; // Default icon for other types
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    // Để tải xuống, có thể mở trong tab mới hoặc tạo một thẻ <a> ẩn
    window.open(fileUrl, '_blank');
  };


  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("..")}
        sx={{ mb: 3 }}
      >
        Back to All Workshops
      </Button>

      <Typography variant="h3" gutterBottom fontWeight={500}>
        {workshop.title}
      </Typography>

      <CardMedia
        component="img"
        height={500}
        image={workshop.image_url || "/placeholder.svg?height=400&width=800"} // Fallback image
        alt={workshop.title}
        sx={{ bgcolor: "#ddd", mb: 4, borderRadius: 5, objectFit: "fill" }}
      />

      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          {/* Host Info */}
          {workshop.hosts && workshop.hosts.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "text.primary",
                  borderBottom: 2,
                  borderColor: "divider",
                  pb: 1,
                  mb: 2.5,
                }}
              >
                Host(s)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Avatar
                  src="/placeholder.svg?height=80&width=80"
                  sx={{ width: 80, height: 80 }}
                />
                <Box>
                  <Typography variant="h6">
                    {workshop.hosts[0].host_name}
                  </Typography>
                  {workshop.hosts[0].host_organization && (
                      <Typography
                          variant="body2"
                          sx={{ color: "primary.main", fontWeight: 500 }}
                      >
                          {workshop.hosts[0].host_organization}
                      </Typography>
                  )}
                  {workshop.hosts.length > 1 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          and {workshop.hosts.length - 1} other(s)
                      </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Workshop Summary */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "text.primary",
                borderBottom: 2,
                borderColor: "divider",
                pb: 1,
                mb: 2.5,
              }}
            >
              Workshop Summary
            </Typography>
            <Typography variant="body1" paragraph>
              {workshop.description}
            </Typography>
          </Box>

          {/* Learning Objectives */}
          {learningObjectives.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "text.primary",
                  borderBottom: 2,
                  borderColor: "divider",
                  pb: 1,
                  mb: 2.5,
                }}
              >
                Learning Objectives
              </Typography>
              <List>
                {learningObjectives.map((objective: string, index: number) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={objective} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Who Should Attend */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "text.primary",
                borderBottom: 2,
                borderColor: "divider",
                pb: 1,
                mb: 2.5,
              }}
            >
              Who Should Attend
            </Typography>
            <Typography variant="body1">{workshop.target_audience}</Typography>
          </Box>

          {/* About the Host */}
          {workshop.hosts && workshop.hosts.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "text.primary",
                  borderBottom: 2,
                  borderColor: "divider",
                  pb: 1,
                  mb: 2.5,
                }}
              >
                About the Host(s)
              </Typography>
              {workshop.hosts.map((host: IHost, index: number) => (
                <Card
                  key={index}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    boxShadow: "none",
                    mb: 2,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}
                    >
                      <Avatar
                        src="/placeholder.svg?height=160&width=160"
                        sx={{ width: 80, height: 80 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.25rem",
                            color: "text.primary",
                            mb: 0.5,
                          }}
                        >
                          {host.host_name}
                        </Typography>
                        {host.host_organization && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "primary.main",
                              fontWeight: 500,
                              mb: 1.25,
                            }}
                          >
                            {host.host_organization}
                          </Typography>
                        )}
                        {host.host_bio && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.95rem",
                              lineHeight: 1.6,
                            }}
                          >
                            {host.host_bio}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Box>
            {workshop.calculated_status == STATUS_COMPLETED && workshop.video_recording_url && (
              <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      color: "text.primary",
                      borderBottom: 2,
                      borderColor: "divider",
                      pb: 1,
                      mb: 2.5,
                    }}
                  >
                    Workshop Recording
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#000",
                      borderRadius: 2,
                      height: 350,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={workshop.video_recording_url.replace("watch?v=", "embed/")} // Chuyển đổi URL YouTube nếu có
                      title="Workshop Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </Paper>
              </Box>
            )}

            {/* Downloadable Materials Section */}
            {workshop.attached_documents && workshop.attached_documents.length > 0 && (
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: "text.primary",
                    borderBottom: 2,
                    borderColor: "divider",
                    pb: 1,
                    mb: 2.5,
                  }}
                >
                  Workshop Resources
                </Typography>

                <Paper elevation={0} sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}>
                  <List sx={{ p: 0 }}>
                      {workshop.attached_documents.map((file: any, index: any) => (
                      <ListItem
                        key={index}
                        sx={{
                          py: 2,
                          px: 3,
                          borderBottom: index < workshop.attached_documents.length - 1 ? "1px solid #e0e0e0" : "none",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>{getFileIcon(file.file_type)}</ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 500, color: "#424242" }}>
                              {file.file_name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ color: "#757575", mt: 0.5 }}>
                              {file.file_size ? `${(file.file_size / 1024).toFixed(2)} KB` : ''}
                            </Typography>
                          }
                        />
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={() => handleDownload(file.file_url, file.file_name)}
                          sx={{
                            backgroundColor: "#2196f3",
                            color: "white",
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            "&:hover": {
                              backgroundColor: "#1976d2",
                            },
                          }}
                        >
                          Download
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}
          </Box>
          
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 40 }}>
            <CardContent sx={{ p: 4 }}>
              {isCurrentUserTheCreator ? (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleEdit}
                  startIcon={<Edit />}
                  sx={{ mb: 3 }}
                >
                  Edit Workshop
                </Button>
              ) : (
                <Button
                  variant={registered ? "outlined" : "contained"}
                  fullWidth
                  size="large"
                  onClick={handleRegistration}
                  disabled={isButtonDisabled || isSubmitting}
                  startIcon={buttonIcon}
                  sx={{ mb: 3 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    buttonText
                  )}
                </Button>
              )}

              <List>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={formatDate(workshop.date)} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${new Date(workshop.date).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      // hour12: false
                    })} (Your Timezone)`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AccessTimeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={`${workshop.duration} Minutes`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      workshop.format === "online" ? "Online" : workshop.format
                    }
                  />
                </ListItem>
              </List>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShareIcon />}
                onClick={handleShare}
              >
                Share Workshop
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Registration Dialog */}
      <Dialog
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          <Box sx={{ fontSize: "3rem", mb: 2 }}>🚀</Box>
          Register for {workshop.title}!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: "center", mb: 3 }}>
            Sign up to register for "{workshop.title}" and get access to all
            future workshops.
          </Typography>
          {/* Form fields would go here */}
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() =>
              window.open(
                `https://${process.env.API_HOST}/${process.env.PREFIX_EDUCATOR_REGISTER}`,
                "_blank"
              )
            }
          >
            Sign Up
          </Button>
          <Typography variant="body2" sx={{ textAlign: "center", mt: 1 }}>
            Already have an account?{" "}
            <Link
              component="button"
              variant="body2"
              onClick={() => (window.location.href = user?.login_url || "")}
            >
              Sign In
            </Link>
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          <Box sx={{ fontSize: "3rem", mb: 2 }}>✅</Box>
          {"You're Registered!"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: "center", mb: 3 }}>
            A confirmation for "{workshop.title}" has been sent to your email.
          </Typography>
        </DialogContent>
        {/* <DialogActions sx={{ flexDirection: "column", gap: 2, p: 3 }}>
          <Button variant="contained" fullWidth startIcon={<AddIcon />}>
            Add to Google Calendar
          </Button>
          <Button variant="outlined" fullWidth startIcon={<EmailIcon />}>
            Invite a Friend
          </Button>
          <Button onClick={() => setSuccessOpen(false)}>Close</Button>
        </DialogActions> */}
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share this Workshop</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            Let your network know about this learning opportunity!
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Tooltip title="Copy Link">
              <IconButton onClick={handleCopyLink} size="large" color="primary">
                <ContentCopyIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on Facebook">
              <IconButton onClick={handleShareToFacebook} size="large" sx={{ color: '#1877F2' }}>
                <FacebookIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on X (Twitter)">
              <IconButton onClick={handleShareToTwitter} size="large" sx={{ color: '#1DA1F2' }}>
                <TwitterIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on LinkedIn">
              <IconButton onClick={handleShareToLinkedIn} size="large" sx={{ color: '#0A66C2' }}>
                <LinkedInIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Share via Email">
              <IconButton onClick={handleShareViaEmail} size="large" color="secondary">
                <EmailIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip> */}
            {/* <Tooltip title="Share on WhatsApp">
              <IconButton onClick={handleShareToWhatsApp} size="large" sx={{ color: '#25D366' }}>
                <WhatsAppIcon sx={{ fontSize: "2.5rem" }} />
              </IconButton>
            </Tooltip> */}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
